export type TokenSocialKind = 'x' | 'youtube' | 'facebook' | 'instagram' | 'tiktok' | 'telegram' | 'discord'

export interface TokenSocials {
  x?: string
  youtube?: string
  facebook?: string
  instagram?: string
  tiktok?: string
  telegram?: string
  discord?: string
}

export const TOKEN_SOCIAL_FIELDS: { key: TokenSocialKind, label: string, placeholder: string }[] = [
  { key: 'x', label: 'X.com', placeholder: 'https://x.com/' },
  { key: 'youtube', label: 'Youtube', placeholder: 'https://youtube.com/' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/' },
  { key: 'telegram', label: 'Telegram', placeholder: 'https://t.me/' },
  { key: 'discord', label: 'Discord', placeholder: 'https://discord.gg/' },
]

const UNSAFE_URL_SCHEME = /^\s*[a-z][a-z0-9+.-]*:/i

// Defense-in-depth: cap stored link length so a malicious/careless input can't
// abuse storage or downstream rendering with an oversized value. Real social
///website URLs are always far shorter than this.
const MAX_URL_LENGTH = 500

export function trimOptionalUrl(value?: string): string | undefined {
  const trimmed = value?.trim() ?? ''
  if (!trimmed)
    return undefined
  if (trimmed.length > MAX_URL_LENGTH)
    return undefined

  // Reject any explicit non-http(s) scheme (javascript:, data:, vbscript:, file:, etc.)
  // to prevent stored malicious links from being rendered as clickable hrefs to visitors.
  const schemeMatch = trimmed.match(UNSAFE_URL_SCHEME)
  if (schemeMatch && !/^https?:$/i.test(schemeMatch[0].replace(/\s+/g, ''))) {
    return undefined
  }

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  if (candidate.length > MAX_URL_LENGTH)
    return undefined

  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      return undefined
    return candidate
  }
  catch {
    return undefined
  }
}

export function pickTokenSocials(raw?: TokenSocials | null): TokenSocials {
  const next: TokenSocials = {}
  if (!raw)
    return next
  for (const field of TOKEN_SOCIAL_FIELDS) {
    const value = trimOptionalUrl(raw[field.key])
    if (value)
      next[field.key] = value
  }
  return next
}

export function optionalTokenSocials(raw?: TokenSocials | null): TokenSocials | undefined {
  const next = pickTokenSocials(raw)
  return TOKEN_SOCIAL_FIELDS.some(field => next[field.key]) ? next : undefined
}

export function filledTokenSocials(socials?: TokenSocials | null): { key: TokenSocialKind, label: string, href: string }[] {
  if (!socials)
    return []
  return TOKEN_SOCIAL_FIELDS.flatMap((field) => {
    const href = trimOptionalUrl(socials[field.key])
    return href ? [{ key: field.key, label: field.label, href }] : []
  })
}

export function projectToTokenSocials(project?: {
  twitter?: string
  youtube?: string
  facebook?: string
  instagram?: string
  tiktok?: string
  telegram?: string
  discord?: string
} | null): TokenSocials {
  if (!project)
    return {}
  return pickTokenSocials({
    x: project.twitter,
    youtube: project.youtube,
    facebook: project.facebook,
    instagram: project.instagram,
    tiktok: project.tiktok,
    telegram: project.telegram,
    discord: project.discord,
  })
}
